/**
 * Sync helpers between the on-template canvas and form state.
 * Works with data-field markers and with common template CSS classes.
 */

const SCALAR_FIELDS = [
  'name',
  'title',
  'email',
  'phone',
  'location',
  'linkedin',
  'website',
  'github',
  'summary',
];

/** CSS selectors that usually hold scalar CV text in catalog templates. */
const SCALAR_SELECTORS = {
  name: ['.name', '.sidebar-name', 'h1.name', '.cv-name'],
  title: ['.title', '.sidebar-title', '.job-headline', '.header-title', '.cv-title'],
  summary: ['.summary-text', '.summary', '.about-text', '.profile-text'],
  email: ['.email', '.contact-email'],
  phone: ['.phone', '.contact-phone'],
  location: ['.location', '.contact-location'],
  linkedin: ['.linkedin', '.contact-linkedin'],
  website: ['.website', '.contact-website'],
  github: ['.github', '.contact-github'],
};

/**
 * Visible text of a field node (keeps line breaks for multiline).
 *
 * @param {Element} el
 * @returns {string}
 */
export const fieldPlainText = (el) => {
  if (!el) {
    return '';
  }
  const raw = el.innerText != null && String(el.innerText).trim() !== ''
    ? el.innerText
    : (el.textContent || '');
  return String(raw)
    .replace(/\u00a0/g, ' ')
    .replace(/\r\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

/**
 * Put data-field on known containers when the filled HTML lacks markers.
 *
 * @param {ParentNode} root
 */
export const annotateSyncFields = (root) => {
  if (!root || !root.querySelectorAll) {
    return;
  }

  const ensureField = (el, field) => {
    if (!el || !field) {
      return;
    }
    if (el.getAttribute('data-field') === field) {
      return;
    }
    if (el.querySelector(`[data-field="${field}"]`)) {
      return;
    }
    el.setAttribute('data-field', field);
  };

  Object.entries(SCALAR_SELECTORS).forEach(([field, selectors]) => {
    selectors.forEach((selector) => {
      root.querySelectorAll(selector).forEach((el) => ensureField(el, field));
    });
  });

  root.querySelectorAll('.contact-item').forEach((item) => {
    if (item.querySelector('[data-field]')) {
      return;
    }
    const text = fieldPlainText(item);
    let field = '';
    if (/@/.test(text)) {
      field = 'email';
    } else if (/linkedin\.com|\bin\/[a-z0-9-]+/i.test(text)) {
      field = 'linkedin';
    } else if (/github\.com/i.test(text)) {
      field = 'github';
    } else if (/^[\d\s+().-]{6,}$/.test(text.replace(/\s/g, ' ').trim())
      || /^\+?\d/.test(text)) {
      field = 'phone';
    } else if (text) {
      field = 'location';
    }
    if (!field) {
      return;
    }
    const valueNode = item.querySelector('span:not(.contact-icon)') || item;
    ensureField(valueNode, field);
  });

  root.querySelectorAll('.experience-item').forEach((item, index) => {
    ensureField(item.querySelector('.job-title'), `experience.${index}.title`);
    ensureField(item.querySelector('.job-company'), `experience.${index}.company`);
    ensureField(item.querySelector('.job-description'), `experience.${index}.description`);
    const meta = item.querySelector('.job-meta');
    if (meta && !meta.querySelector('[data-field]')) {
      ensureField(meta, `experience.${index}.duration`);
    }
  });

  root.querySelectorAll('.education-item').forEach((item, index) => {
    ensureField(item.querySelector('.edu-degree'), `education.${index}.degree`);
    ensureField(item.querySelector('.edu-school'), `education.${index}.institution`);
    ensureField(item.querySelector('.edu-date'), `education.${index}.date`);
    ensureField(item.querySelector('.edu-description'), `education.${index}.description`);
  });

  const skillsHost = root.querySelector(
    '.sidebar-content, .skills-content, .skills-list, .skills',
  );
  if (skillsHost && !root.querySelector('[data-field="skills"], [data-field^="skills."]')) {
    const section = skillsHost.closest('.sidebar-section, .section');
    const title = fieldPlainText(
      section?.querySelector('.sidebar-section-title, .section-title'),
    ).toLowerCase();
    if (!title || /skill|habilid|competenc/i.test(title)) {
      ensureField(skillsHost, 'skills');
    }
  }

  root.querySelectorAll('.achievements-list li, .achievement-item').forEach((el, index) => {
    ensureField(el, `achievements.${index}`);
  });
  root.querySelectorAll('.certification-item').forEach((el, index) => {
    ensureField(el, `certifications.${index}`);
  });
};

/**
 * Read values for each data-field path (container text wins over nested spans).
 *
 * @param {ParentNode|null} root
 * @returns {Map<string, string>}
 */
export const collectDataFields = (root) => {
  const values = new Map();
  if (!root || !root.querySelectorAll) {
    return values;
  }
  annotateSyncFields(root);
  root.querySelectorAll('[data-field]').forEach((el) => {
    const key = el.getAttribute('data-field');
    if (!key) {
      return;
    }
    // Prefer the outermost node for a path so sibling text from contentEditable is kept.
    let ancestor = el.parentElement;
    while (ancestor && ancestor !== root) {
      if (ancestor.getAttribute && ancestor.getAttribute('data-field') === key) {
        return;
      }
      ancestor = ancestor.parentElement;
    }
    if (!values.has(key)) {
      values.set(key, fieldPlainText(el));
    }
  });

  // Class containers are the source of truth for scalars (contentEditable often
  // leaves stale text inside nested spans while new chars sit on the parent).
  Object.entries(SCALAR_SELECTORS).forEach(([field, selectors]) => {
    for (let i = 0; i < selectors.length; i += 1) {
      const el = root.querySelector(selectors[i]);
      if (!el) {
        continue;
      }
      const text = fieldPlainText(el);
      if (text) {
        values.set(field, text);
        break;
      }
    }
  });

  return values;
};

/**
 * Split a skills/languages blob into a string array.
 *
 * @param {string} text
 * @returns {string[]}
 */
const splitListBlob = (text) => {
  if (!text) {
    return [];
  }
  if (text.includes('\n')) {
    return text
      .split(/\n+/)
      .map((part) => part.replace(/^[,•|\-\s]+/, '').trim())
      .filter(Boolean);
  }
  return text
    .split(/[,•|]/)
    .map((part) => part.trim())
    .filter(Boolean);
};

/**
 * Merge canvas field values into editableContent without dropping photo.
 *
 * @param {ParentNode|null} root
 * @param {object} previous
 * @returns {object}
 */
export const extractContentFromEditor = (root, previous = {}) => {
  const values = collectDataFields(root);
  if (values.size === 0) {
    return previous;
  }

  const next = {
    ...previous,
    experience: Array.isArray(previous.experience)
      ? previous.experience.map((item) => (item && typeof item === 'object' ? { ...item } : item))
      : [],
    education: Array.isArray(previous.education)
      ? previous.education.map((item) => (item && typeof item === 'object' ? { ...item } : item))
      : [],
    skills: Array.isArray(previous.skills) ? [...previous.skills] : [],
    languages: Array.isArray(previous.languages) ? [...previous.languages] : [],
    certifications: Array.isArray(previous.certifications) ? [...previous.certifications] : [],
    achievements: Array.isArray(previous.achievements) ? [...previous.achievements] : [],
  };

  SCALAR_FIELDS.forEach((field) => {
    if (values.has(field)) {
      next[field] = values.get(field);
    }
  });

  if (values.has('skills')) {
    next.skills = splitListBlob(values.get('skills'));
  } else {
    const skillParts = [];
    values.forEach((text, key) => {
      const match = /^skills\.(\d+)$/.exec(key);
      if (match) {
        skillParts[Number(match[1])] = text;
      }
    });
    if (skillParts.length) {
      next.skills = skillParts.filter((part) => part != null && String(part).trim());
    }
  }

  if (values.has('languages')) {
    next.languages = splitListBlob(values.get('languages'));
  }
  if (values.has('interests')) {
    next.interests = splitListBlob(values.get('interests'));
  }

  // Experience / education from item lists (more reliable than broken nested spans).
  root.querySelectorAll('.experience-item').forEach((item, index) => {
    while (next.experience.length <= index) {
      next.experience.push({
        title: '', company: '', duration: '', location: '', description: '',
      });
    }
    if (!next.experience[index] || typeof next.experience[index] !== 'object') {
      next.experience[index] = {
        title: '', company: '', duration: '', location: '', description: '',
      };
    }
    const title = item.querySelector('.job-title');
    const company = item.querySelector('.job-company');
    const meta = item.querySelector('.job-meta');
    const description = item.querySelector('.job-description');
    if (title) {
      next.experience[index].title = fieldPlainText(title);
    }
    if (company) {
      next.experience[index].company = fieldPlainText(company);
    }
    if (meta) {
      next.experience[index].duration = fieldPlainText(meta);
    }
    if (description) {
      next.experience[index].description = fieldPlainText(description);
    }
  });

  root.querySelectorAll('.education-item').forEach((item, index) => {
    while (next.education.length <= index) {
      next.education.push({
        degree: '', institution: '', date: '', location: '', description: '',
      });
    }
    if (!next.education[index] || typeof next.education[index] !== 'object') {
      next.education[index] = {
        degree: '', institution: '', date: '', location: '', description: '',
      };
    }
    const degree = item.querySelector('.edu-degree');
    const school = item.querySelector('.edu-school');
    const date = item.querySelector('.edu-date');
    const description = item.querySelector('.edu-description');
    if (degree) {
      next.education[index].degree = fieldPlainText(degree);
    }
    if (school) {
      next.education[index].institution = fieldPlainText(school);
    }
    if (date) {
      next.education[index].date = fieldPlainText(date);
    }
    if (description) {
      next.education[index].description = fieldPlainText(description);
    }
  });

  values.forEach((text, key) => {
    let match = /^experience\.(\d+)\.(title|company|duration|location|description)$/.exec(key);
    if (match && !root.querySelectorAll('.experience-item').length) {
      const index = Number(match[1]);
      while (next.experience.length <= index) {
        next.experience.push({
          title: '', company: '', duration: '', location: '', description: '',
        });
      }
      if (!next.experience[index] || typeof next.experience[index] !== 'object') {
        next.experience[index] = {
          title: '', company: '', duration: '', location: '', description: '',
        };
      }
      next.experience[index][match[2]] = text;
      return;
    }
    match = /^education\.(\d+)\.(degree|institution|date|location|description)$/.exec(key);
    if (match && !root.querySelectorAll('.education-item').length) {
      const index = Number(match[1]);
      while (next.education.length <= index) {
        next.education.push({
          degree: '', institution: '', date: '', location: '', description: '',
        });
      }
      if (!next.education[index] || typeof next.education[index] !== 'object') {
        next.education[index] = {
          degree: '', institution: '', date: '', location: '', description: '',
        };
      }
      next.education[index][match[2]] = text;
      return;
    }
    match = /^achievements\.(\d+)$/.exec(key);
    if (match) {
      next.achievements[Number(match[1])] = text;
      return;
    }
    match = /^certifications\.(\d+)$/.exec(key);
    if (match) {
      next.certifications[Number(match[1])] = text;
    }
  });

  next.achievements = next.achievements.filter((item) => item != null && String(item).trim());
  next.certifications = next.certifications.filter((item) => item != null && String(item).trim());

  return next;
};

/**
 * Write text into matching nodes; prefer data-field, else class selectors.
 *
 * @param {ParentNode} root
 * @param {string} path
 * @param {string} text
 * @param {string[]} [extraSelectors]
 */
const setFieldNodes = (root, path, text, extraSelectors = []) => {
  const value = text == null ? '' : String(text);
  const nodes = [
    ...root.querySelectorAll(`[data-field="${path}"]`),
  ];
  extraSelectors.forEach((selector) => {
    root.querySelectorAll(selector).forEach((el) => nodes.push(el));
  });
  const unique = [...new Set(nodes)];
  unique.forEach((el) => {
    if (fieldPlainText(el) === value.trim()) {
      return;
    }
    // Keep icon siblings in contact rows: update the value span when present.
    if (el.classList?.contains('contact-item')) {
      const valueNode = el.querySelector('[data-field], span:not(.contact-icon)') || el;
      if (valueNode !== el) {
        valueNode.textContent = value;
        return;
      }
    }
    const nested = el.querySelector(`[data-field="${path}"]`);
    if (nested) {
      nested.textContent = value;
      return;
    }
    el.textContent = value;
  });
};

/**
 * Push form values into the live canvas.
 *
 * @param {ParentNode|null} root
 * @param {object} content
 */
export const patchEditorFields = (root, content) => {
  if (!root || !content || !root.querySelectorAll) {
    return;
  }
  annotateSyncFields(root);

  SCALAR_FIELDS.forEach((field) => {
    if (!Object.prototype.hasOwnProperty.call(content, field)) {
      return;
    }
    setFieldNodes(root, field, content[field] || '', SCALAR_SELECTORS[field] || []);
  });

  if (Array.isArray(content.skills)) {
    if (root.querySelector('[data-field="skills"]')) {
      setFieldNodes(root, 'skills', content.skills.join(', '));
    } else {
      content.skills.forEach((skill, index) => setFieldNodes(root, `skills.${index}`, skill));
    }
  }

  if (Array.isArray(content.languages)) {
    const langText = content.languages
      .map((lang) => {
        if (lang && typeof lang === 'object') {
          const name = lang.name || '';
          const level = lang.level || '';
          return level ? `${name} (${level})` : name;
        }
        return String(lang || '');
      })
      .filter(Boolean)
      .join('\n');
    setFieldNodes(root, 'languages', langText);
  }

  if (Array.isArray(content.interests)) {
    setFieldNodes(root, 'interests', content.interests.join(', '));
  }

  (content.experience || []).forEach((exp, index) => {
    if (!exp || typeof exp !== 'object') {
      return;
    }
    ['title', 'company', 'duration', 'location', 'description'].forEach((field) => {
      if (Object.prototype.hasOwnProperty.call(exp, field)) {
        setFieldNodes(root, `experience.${index}.${field}`, exp[field] || '');
      }
    });
  });

  (content.education || []).forEach((edu, index) => {
    if (!edu || typeof edu !== 'object') {
      return;
    }
    ['degree', 'institution', 'date', 'location', 'description'].forEach((field) => {
      if (Object.prototype.hasOwnProperty.call(edu, field)) {
        setFieldNodes(root, `education.${index}.${field}`, edu[field] || '');
      }
    });
  });

  (content.achievements || []).forEach((item, index) => {
    setFieldNodes(root, `achievements.${index}`, item);
  });

  (content.certifications || []).forEach((item, index) => {
    setFieldNodes(root, `certifications.${index}`, item);
  });
};

/**
 * Shallow compare of synced text fields (ignore photo File identity noise).
 *
 * @param {object} a
 * @param {object} b
 * @returns {boolean}
 */
export const syncedContentEqual = (a, b) => {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  try {
    const strip = (obj) => {
      const { photo, ...rest } = obj;
      return rest;
    };
    return JSON.stringify(strip(a)) === JSON.stringify(strip(b));
  } catch (err) {
    return false;
  }
};
